/**
 * Normalizes a rating value from display/internal format to the server's
 * expected format. The server stores "DOES_NOT_MEET" (underscore).
 *
 * @param {string|null} rating
 * @returns {string|null}
 */
export const normalizeRatingForServer = (rating) => {
    if (rating === "DOES NOT MEET") return "DOES_NOT_MEET";
    return rating ?? null;
};

/**
 * Builds the flat RatingRecord array sent to the server on every save.
 *
 * Strategy: always sends FULL state of all students/items — not a delta.
 * The server handles upsert: existing key = update, null key = create.
 *
 * Each record's gradingCriteria comes from:
 *   1. The existing rating entry on the student (most reliable — already correct)
 *   2. The student's resolved gradingCriteriaId from the ratingMap entry
 *
 * New ratings (no existing entry, key: null) must carry the correct
 * gradingCriteria — callers must ensure ratingMap entries include it.
 *
 * @param {Object} params
 * @param {Map<string, RatingMapEntry>} params.ratingMap
 *   Key format: `${gradingProfileStudentId}_${templateContentId}`
 *   Value: {
 *     gradingProfileStudentId: number,
 *     gradingCriteria: number,
 *     templateContentId: number,
 *     contentType: string,
 *     rating: string | null,
 *     comment: string | null,
 *     existingKey: string | null,   // null = new record
 *     personalizedLearningPlan: boolean,
 *   }
 * @param {number} params.raterGuid - current user's guid (lastRatedGuid)
 *
 * @returns {RatingRecord[]}
 *   Each record: {
 *     key, gradingProfileStudentId, gradingCriteria, templateContentId,
 *     contentType, rating, comment, personalizedLearningPlan, lastRatedGuid
 *   }
 */
const buildRatingPayload = ({ ratingMap, raterGuid }) => {
    const records = [];

    ratingMap.forEach((entry) => {
        const normalizedRating = normalizeRatingForServer(entry.rating);

        // Skip new records with no rating — nothing to persist
        if (entry.existingKey === null && (normalizedRating === null || normalizedRating === "")) {
            return;
        }

        records.push({
            key: entry.existingKey ?? null,
            gradingProfileStudentId: entry.gradingProfileStudentId,
            gradingCriteria: entry.gradingCriteria,
            templateContentId: entry.templateContentId,
            contentType: entry.contentType,
            rating: normalizedRating,
            comment: entry.comment ?? null,
            personalizedLearningPlan: entry.personalizedLearningPlan ?? false,
            lastRatedGuid: raterGuid
        });
    });

    return records;
};

export default buildRatingPayload;
